import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../App';
import { Button } from '@/components/ui/button';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';
import { Plus, Send, Play, Pause, Pencil, ListChecks, Trash2, FileText, Mail, Download, Save, X, Package, FileArchive } from 'lucide-react';
import { Progress } from '@/components/ui/progress';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

const initialForm = {
  source_type: 'manual',
  batch_id: '',
  crossfile_id: '',
  single_file_id: '',
  mail_config_id: '',
  mode: 'link',
  recipients: '',
  save_copy_on_sent: false,
  subject: '',
  body_html: '<p>Hello,</p><p></p>',
};

export default function SendBatches() {
  const navigate = useNavigate();
  const [sendBatches, setSendBatches] = useState([]);
  const [open, setOpen] = useState(false);
  const [editingBatch, setEditingBatch] = useState(null);
  const [batches, setBatches] = useState([]);
  const [allCrossfiles, setAllCrossfiles] = useState([]);
  const [batchFiles, setBatchFiles] = useState([]);
  const [crossfiles, setCrossfiles] = useState([]);
  const [mailConfigs, setMailConfigs] = useState([]);
  const [formData, setFormData] = useState(initialForm);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deletingBatch, setDeletingBatch] = useState(null);
  const [mappingRows, setMappingRows] = useState([]);
  const [rowDrafts, setRowDrafts] = useState({});
  const [newMappingRow, setNewMappingRow] = useState({ recipient_email: '', nameinfile: '' });
  const [mappingBusy, setMappingBusy] = useState(false);
  const [mappingDeleteDialogOpen, setMappingDeleteDialogOpen] = useState(false);
  const [mappingDeleteTarget, setMappingDeleteTarget] = useState(null);
  const [mapBatchId, setMapBatchId] = useState('');
  const [mapBatchFiles, setMapBatchFiles] = useState([]);
  const [mapCrossfiles, setMapCrossfiles] = useState([]);
  const [mapCrossfileId, setMapCrossfileId] = useState('');
  const [standaloneRows, setStandaloneRows] = useState([]);
  const [standaloneDrafts, setStandaloneDrafts] = useState({});
  const [standaloneNewRow, setStandaloneNewRow] = useState({ recipient_email: '', nameinfile: '' });
  const [showAddMappingForm, setShowAddMappingForm] = useState(false);

  useEffect(() => {
    loadSendBatches();
    loadBatchesAndMappings();
    api.get('/mail-configs').then(res => setMailConfigs(res.data));
    const interval = setInterval(loadSendBatches, 5000);
    return () => clearInterval(interval);
  }, []);

  const loadSendBatches = () => {
    api.get('/send-batches').then(res => setSendBatches(res.data));
  };

  const loadBatchesAndMappings = async () => {
    const [batchesRes, crossfilesRes] = await Promise.all([
      api.get('/batches'),
      api.get('/crossfiles').catch(() => ({ data: [] })),
    ]);
    setBatches(batchesRes.data || []);
    setAllCrossfiles(crossfilesRes.data || []);
  };

  const resetForm = () => {
    setFormData(initialForm);
    setBatchFiles([]);
    setCrossfiles([]);
    setMappingRows([]);
    setRowDrafts({});
    setNewMappingRow({ recipient_email: '', nameinfile: '' });
    setMappingDeleteTarget(null);
    setEditingBatch(null);
  };

  const parseRecipients = (value = '') => (
    String(value || '')
      .split(/[\n,;]+/)
      .map(email => email.trim())
      .filter(Boolean)
  );

  const loadBatchAssets = async (batchId) => {
    if (!batchId) {
      setBatchFiles([]);
      setCrossfiles([]);
      return;
    }
    const [filesRes, crossfilesRes] = await Promise.all([
      api.get(`/batches/${batchId}/files`),
      api.get(`/crossfiles?batch_id=${batchId}`).catch(() => ({ data: [] })),
    ]);
    setBatchFiles(filesRes.data || []);
    setCrossfiles(crossfilesRes.data || []);
  };

  const loadStandaloneBatchAssets = async (batchId, keepSelected = false) => {
    if (!batchId) {
      setMapBatchFiles([]);
      setMapCrossfiles([]);
      setMapCrossfileId('');
      setStandaloneRows([]);
      setStandaloneDrafts({});
      return;
    }
    const [filesRes, crossfilesRes] = await Promise.all([
      api.get(`/batches/${batchId}/files`),
      api.get(`/crossfiles?batch_id=${batchId}`).catch(() => ({ data: [] })),
    ]);
    setMapBatchFiles(filesRes.data || []);
    setMapCrossfiles(crossfilesRes.data || []);
    if (!keepSelected) {
      setMapCrossfileId('');
      setStandaloneRows([]);
      setStandaloneDrafts({});
    }
  };

  const handleStandaloneBatchSelect = async (batchId) => {
    setMapBatchId(batchId);
    try {
      await loadStandaloneBatchAssets(batchId);
    } catch {
      toast.error('Failed to load mapping batches');
    }
  };

  const handleBatchSelect = async (batchId) => {
    setFormData({...formData, batch_id: batchId, crossfile_id: '', single_file_id: ''});
    setMappingRows([]);
    setRowDrafts({});
    try {
      await loadBatchAssets(batchId);
    } catch {
      toast.error('Failed to load batch files');
    }
  };

  const uploadCrossfile = async (e) => {
    const file = e.target.files[0];
    if (!file || !formData.batch_id) return;

    const fd = new FormData();
    fd.append('batch_id', formData.batch_id);
    fd.append('file', file);

    try {
      const res = await api.post('/crossfiles/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success(`Map uploaded and saved. Matched: ${res.data.matched_count}, Missing: ${res.data.missing_count}`);
      setFormData({...formData, crossfile_id: res.data.crossfile_id});
      await loadBatchAssets(formData.batch_id);
      await loadMappingRows(res.data.crossfile_id);
      await loadBatchesAndMappings();
    } catch {
      toast.error('Crossfile upload failed');
    }
  };

  const uploadStandaloneCrossfile = async (e) => {
    const file = e.target.files[0];
    if (!file || !mapBatchId) return;

    const fd = new FormData();
    fd.append('batch_id', mapBatchId);
    fd.append('file', file);

    try {
      const res = await api.post('/crossfiles/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success(`Map uploaded and saved. Matched: ${res.data.matched_count}, Missing: ${res.data.missing_count}`);
      setMapCrossfileId(res.data.crossfile_id);
      await loadStandaloneBatchAssets(mapBatchId, true);
      await loadStandaloneRows(res.data.crossfile_id);
      await loadBatchesAndMappings();
      setShowAddMappingForm(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Mapping upload failed');
    } finally {
      e.target.value = '';
    }
  };

  const loadMappingRows = async (crossfileId) => {
    if (!crossfileId) {
      setMappingRows([]);
      setRowDrafts({});
      return;
    }
    try {
      const res = await api.get(`/crossfiles/${crossfileId}/rows`);
      const rows = res.data || [];
      setMappingRows(rows);
      setRowDrafts(Object.fromEntries(rows.map(row => [row.id, {
        recipient_email: row.recipient_email,
        nameinfile: row.nameinfile,
      }])));
    } catch {
      toast.error('Failed to load mapping preview');
      setMappingRows([]);
      setRowDrafts({});
    }
  };

  const loadStandaloneRows = async (crossfileId) => {
    if (!crossfileId) {
      setStandaloneRows([]);
      setStandaloneDrafts({});
      return;
    }
    try {
      const res = await api.get(`/crossfiles/${crossfileId}/rows`);
      const rows = res.data || [];
      setStandaloneRows(rows);
      setStandaloneDrafts(Object.fromEntries(rows.map(row => [row.id, {
        recipient_email: row.recipient_email,
        nameinfile: row.nameinfile,
      }])));
    } catch {
      toast.error('Failed to load mapping rows');
      setStandaloneRows([]);
      setStandaloneDrafts({});
    }
  };

  const handleCrossfileSelect = async (crossfileId) => {
    setFormData({...formData, crossfile_id: crossfileId});
    await loadMappingRows(crossfileId);
  };

  const handleStandaloneCrossfileSelect = async (crossfileId) => {
    setMapCrossfileId(crossfileId);
    await loadStandaloneRows(crossfileId);
  };

  const editExistingMapping = async (crossfile) => {
    setMapBatchId(crossfile.batch_id);
    setMapCrossfileId(crossfile.id);
    try {
      await loadStandaloneBatchAssets(crossfile.batch_id, true);
      await loadStandaloneRows(crossfile.id);
    } catch {
      toast.error('Failed to open mapping');
    }
  };

  const downloadMappingExample = () => {
    const csv = 'mail,nameinfile\nclient.one@example.com,invoice_001.pdf\nclient.two@example.com,contract_002.pdf\n';
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'batchmailer_mapping_example.csv';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const updateRowDraft = (rowId, field, value) => {
    setRowDrafts(prev => ({
      ...prev,
      [rowId]: {
        ...(prev[rowId] || {}),
        [field]: value,
      },
    }));
  };

  const updateStandaloneDraft = (rowId, field, value) => {
    setStandaloneDrafts(prev => ({
      ...prev,
      [rowId]: {
        ...(prev[rowId] || {}),
        [field]: value,
      },
    }));
  };

  const addMappingRow = async () => {
    if (!formData.crossfile_id || !newMappingRow.recipient_email || !newMappingRow.nameinfile) {
      toast.error('Enter recipient email and file name');
      return;
    }
    setMappingBusy(true);
    try {
      await api.post(`/crossfiles/${formData.crossfile_id}/rows`, newMappingRow);
      setNewMappingRow({ recipient_email: '', nameinfile: '' });
      await loadMappingRows(formData.crossfile_id);
      toast.success('Mapping row added');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add mapping row');
    } finally {
      setMappingBusy(false);
    }
  };

  const addStandaloneRow = async () => {
    if (!mapCrossfileId || !standaloneNewRow.recipient_email || !standaloneNewRow.nameinfile) {
      toast.error('Enter recipient email and file name');
      return;
    }
    setMappingBusy(true);
    try {
      await api.post(`/crossfiles/${mapCrossfileId}/rows`, standaloneNewRow);
      setStandaloneNewRow({ recipient_email: '', nameinfile: '' });
      await loadStandaloneRows(mapCrossfileId);
      await loadBatchesAndMappings();
      toast.success('Mapping row added');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add mapping row');
    } finally {
      setMappingBusy(false);
    }
  };

  const saveMappingRow = async (row) => {
    if (!formData.crossfile_id) return;
    const draft = rowDrafts[row.id] || {};
    setMappingBusy(true);
    try {
      await api.put(`/crossfiles/${formData.crossfile_id}/rows/${row.id}`, {
        recipient_email: draft.recipient_email,
        nameinfile: draft.nameinfile,
      });
      await loadMappingRows(formData.crossfile_id);
      toast.success('Mapping row updated');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update mapping row');
    } finally {
      setMappingBusy(false);
    }
  };

  const saveAllMappingRows = async () => {
    if (!formData.crossfile_id || mappingRows.length === 0) return;
    setMappingBusy(true);
    try {
      await Promise.all(mappingRows.map(row => {
        const draft = rowDrafts[row.id] || {};
        return api.put(`/crossfiles/${formData.crossfile_id}/rows/${row.id}`, {
          recipient_email: draft.recipient_email,
          nameinfile: draft.nameinfile,
        });
      }));
      await loadMappingRows(formData.crossfile_id);
      toast.success('All mapping rows saved');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save all mapping rows');
    } finally {
      setMappingBusy(false);
    }
  };

  const saveStandaloneRow = async (row) => {
    if (!mapCrossfileId) return;
    const draft = standaloneDrafts[row.id] || {};
    setMappingBusy(true);
    try {
      await api.put(`/crossfiles/${mapCrossfileId}/rows/${row.id}`, {
        recipient_email: draft.recipient_email,
        nameinfile: draft.nameinfile,
      });
      await loadStandaloneRows(mapCrossfileId);
      await loadBatchesAndMappings();
      toast.success('Mapping row updated');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update mapping row');
    } finally {
      setMappingBusy(false);
    }
  };

  const saveAllStandaloneRows = async () => {
    if (!mapCrossfileId || standaloneRows.length === 0) return;
    setMappingBusy(true);
    try {
      await Promise.all(standaloneRows.map(row => {
        const draft = standaloneDrafts[row.id] || {};
        return api.put(`/crossfiles/${mapCrossfileId}/rows/${row.id}`, {
          recipient_email: draft.recipient_email,
          nameinfile: draft.nameinfile,
        });
      }));
      await loadStandaloneRows(mapCrossfileId);
      await loadBatchesAndMappings();
      toast.success('All mapping rows saved');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save all mapping rows');
    } finally {
      setMappingBusy(false);
    }
  };

  const confirmDeleteMappingRow = (row) => {
    setMappingDeleteTarget({ type: 'row', row, source: 'form' });
    setMappingDeleteDialogOpen(true);
  };

  const confirmDeleteMapping = () => {
    setMappingDeleteTarget({ type: 'mapping', source: 'form' });
    setMappingDeleteDialogOpen(true);
  };

  const confirmDeleteStandaloneRow = (row) => {
    setMappingDeleteTarget({ type: 'row', row, source: 'standalone' });
    setMappingDeleteDialogOpen(true);
  };

  const confirmDeleteStandaloneMapping = () => {
    setMappingDeleteTarget({ type: 'mapping', source: 'standalone' });
    setMappingDeleteDialogOpen(true);
  };

  const deleteMappingTarget = async () => {
    if (!mappingDeleteTarget) return;
    const isStandalone = mappingDeleteTarget.source === 'standalone';
    const crossfileId = isStandalone ? mapCrossfileId : formData.crossfile_id;
    const batchId = isStandalone ? mapBatchId : formData.batch_id;
    if (!crossfileId) return;
    setMappingBusy(true);
    try {
      if (mappingDeleteTarget.type === 'row') {
        await api.delete(`/crossfiles/${crossfileId}/rows/${mappingDeleteTarget.row.id}`);
        if (isStandalone) {
          await loadStandaloneRows(crossfileId);
          await loadBatchesAndMappings();
        } else {
          await loadMappingRows(crossfileId);
          await loadBatchesAndMappings();
        }
        toast.success('Mapping row removed');
      } else {
        await api.delete(`/crossfiles/${crossfileId}`);
        if (isStandalone) {
          setMapCrossfileId('');
          setStandaloneRows([]);
          setStandaloneDrafts({});
          await loadStandaloneBatchAssets(batchId, true);
          await loadBatchesAndMappings();
        } else {
          setFormData({...formData, crossfile_id: ''});
          setMappingRows([]);
          setRowDrafts({});
          await loadBatchAssets(batchId);
          await loadBatchesAndMappings();
        }
        toast.success('Mapping removed');
      }
      setMappingDeleteDialogOpen(false);
      setMappingDeleteTarget(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete mapping');
    } finally {
      setMappingBusy(false);
    }
  };

  const buildPayload = () => {
    const sourceType = formData.source_type;
    return {
      source_type: sourceType,
      batch_id: sourceType === 'mapped_files' ? formData.batch_id : undefined,
      crossfile_id: sourceType === 'mapped_files' ? formData.crossfile_id : undefined,
      single_file_id: sourceType === 'single_file' ? formData.single_file_id : undefined,
      recipients: sourceType === 'mapped_files' ? undefined : parseRecipients(formData.recipients),
      mail_config_id: formData.mail_config_id,
      mode: sourceType === 'manual' ? 'link' : formData.mode,
      save_copy_on_sent: formData.save_copy_on_sent,
      subject: formData.subject,
      body_html: formData.body_html,
    };
  };

  const saveDraft = async (e) => {
    e.preventDefault();
    try {
      const payload = buildPayload();
      if (editingBatch) {
        await api.put(`/send-batches/${editingBatch.id}`, payload);
        toast.success('Draft updated');
      } else {
        await api.post('/send-batches', payload);
        toast.success('Draft created');
      }
      setOpen(false);
      resetForm();
      loadSendBatches();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save batch');
    }
  };

  const queueBatch = async (id) => {
    try {
      await api.post(`/send-batches/${id}/queue`);
      toast.success('Batch queued for review');
      loadSendBatches();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to queue batch');
    }
  };

  const sendBatch = async (id) => {
    try {
      await api.post(`/send-batches/${id}/send`);
      toast.success('Batch sending started');
      loadSendBatches();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send batch');
    }
  };

  const confirmDeleteBatch = (batch) => {
    setDeletingBatch(batch);
    setDeleteDialogOpen(true);
  };

  const deleteSendBatch = async () => {
    if (!deletingBatch) return;
    try {
      await api.delete(`/send-batches/${deletingBatch.id}`);
      toast.success('Send batch deleted');
      setDeleteDialogOpen(false);
      setDeletingBatch(null);
      loadSendBatches();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete send batch');
    }
  };

  const editBatch = async (batch) => {
    try {
      const itemsRes = await api.get(`/send-batches/${batch.id}/items`);
      const items = itemsRes.data || [];
      const sourceType = batch.source_type || (batch.batch_id ? 'mapped_files' : 'manual');
      const firstFileId = items.find(item => item.file_id)?.file_id || '';
      const recipients = items.map(item => item.recipient_email).join('\n');
      setEditingBatch(batch);
      setFormData({
        source_type: sourceType,
        batch_id: batch.batch_id || '',
        crossfile_id: batch.crossfile_id || '',
        single_file_id: sourceType === 'single_file' ? firstFileId : '',
        mail_config_id: batch.mail_config_id || '',
        mode: batch.mode || 'link',
        recipients,
        save_copy_on_sent: !!batch.save_copy_on_sent,
        subject: batch.subject || '',
        body_html: batch.body_html || '<p>Hello,</p><p></p>',
      });
      if (batch.batch_id) {
        await loadBatchAssets(batch.batch_id);
      }
      if (batch.crossfile_id) {
        await loadMappingRows(batch.crossfile_id);
      }
      setOpen(true);
    } catch {
      toast.error('Failed to load batch for editing');
    }
  };

  const sourceNeedsRecipients = formData.source_type !== 'mapped_files';
  const batchById = batches.reduce((map, batch) => {
    map[batch.id] = batch;
    return map;
  }, {});
  const formatBytes = (bytes) => {
    const value = Number(bytes || 0);
    if (value >= 1024 * 1024 * 1024) return `${(value / 1024 / 1024 / 1024).toFixed(2)} GB`;
    if (value >= 1024 * 1024) return `${(value / 1024 / 1024).toFixed(2)} MB`;
    if (value >= 1024) return `${(value / 1024).toFixed(2)} KB`;
    return `${value} B`;
  };
  const selectedSingleFile = batchFiles.find(file => file.id === formData.single_file_id);
  const recipientPreview = parseRecipients(formData.recipients);
  const fileById = batchFiles.reduce((map, file) => {
    map[file.id] = file;
    return map;
  }, {});
  const mappedPreviewRows = mappingRows.map(row => ({
    ...row,
    file_name: row.matched_file_id ? fileById[row.matched_file_id]?.original_filename || row.nameinfile : row.nameinfile
  }));
  const matchedMappingCount = mappedPreviewRows.filter(row => row.match_status === 'matched').length;
  const standaloneFileById = mapBatchFiles.reduce((map, file) => {
    map[file.id] = file;
    return map;
  }, {});
  const standalonePreviewRows = standaloneRows.map(row => ({
    ...row,
    file_name: row.matched_file_id ? standaloneFileById[row.matched_file_id]?.original_filename || row.nameinfile : row.nameinfile
  }));
  const standaloneMatchedCount = standalonePreviewRows.filter(row => row.match_status === 'matched').length;
  const shouldShowPreview = (
    (formData.source_type === 'single_file' && selectedSingleFile && recipientPreview.length > 0) ||
    (formData.source_type === 'mapped_files' && mappedPreviewRows.length > 0)
  );

  return (
    <div className="space-y-6" data-testid="send-batches-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-4xl font-bold text-white" style={{fontFamily: 'Space Grotesk'}}>Send Batches</h1>
          <p className="text-white/80 mt-1">Create a draft, queue it for review, then send when ready.</p>
        </div>
        <Dialog open={open} onOpenChange={(isOpen) => { setOpen(isOpen); if (!isOpen) resetForm(); }}>
          <DialogTrigger asChild>
            <Button data-testid="create-send-batch-button">
              <Plus className="w-4 h-4 mr-2" />
              New Batch
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" onInteractOutside={(e) => e.preventDefault()}>
            <DialogHeader>
              <DialogTitle>{editingBatch ? 'Edit Send Draft' : 'Create Send Draft'}</DialogTitle>
            </DialogHeader>
            <form onSubmit={saveDraft} className="space-y-5">
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label>Mail Configuration</Label>
                  <Select value={formData.mail_config_id} onValueChange={(v) => setFormData({...formData, mail_config_id: v})}>
                    <SelectTrigger data-testid="mail-config-select"><SelectValue placeholder="Choose config..." /></SelectTrigger>
                    <SelectContent>
                      {mailConfigs.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Send Type</Label>
                  <Select value={formData.source_type} onValueChange={(v) => setFormData({...formData, source_type: v, batch_id: '', crossfile_id: '', single_file_id: ''})}>
                    <SelectTrigger data-testid="source-type-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="manual">No file, just email</SelectItem>
                      <SelectItem value="single_file">Attach one file to all recipients</SelectItem>
                      <SelectItem value="mapped_files">Map files to recipients</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {formData.source_type !== 'manual' && (
                <div className="rounded-lg border bg-gray-50 p-4 space-y-4">
                  <div>
                    <Label>File Batch</Label>
                    <Select value={formData.batch_id} onValueChange={handleBatchSelect}>
                      <SelectTrigger data-testid="batch-select"><SelectValue placeholder="Choose batch..." /></SelectTrigger>
                      <SelectContent>
                        {batches.map(b => <SelectItem key={b.id} value={b.id}>{b.original_zip_name || 'Single File'} ({b.file_count} files)</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>

                  {formData.source_type === 'single_file' && (
                    <div>
                      <Label>File to Attach</Label>
                      <Select value={formData.single_file_id} onValueChange={(v) => setFormData({...formData, single_file_id: v})}>
                        <SelectTrigger data-testid="single-file-select"><SelectValue placeholder="Choose file..." /></SelectTrigger>
                        <SelectContent>
                          {batchFiles.map(file => <SelectItem key={file.id} value={file.id}>{file.original_filename}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                  )}

                  {formData.source_type === 'mapped_files' && (
                    <div className="space-y-3">
                      <div>
                        <Label>Existing Mapping</Label>
                        <Select value={formData.crossfile_id} onValueChange={handleCrossfileSelect}>
                          <SelectTrigger data-testid="crossfile-select"><SelectValue placeholder="Choose mapping or upload below..." /></SelectTrigger>
                          <SelectContent>
                            {crossfiles.map(cf => (
                              <SelectItem key={cf.id} value={cf.id}>
                                {cf.original_filename} ({cf.matched_count} matched)
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="grid md:grid-cols-[1fr_auto] gap-3 items-end">
                        <div>
                          <Label>Upload Mapping CSV</Label>
                          <Input type="file" accept=".csv" onChange={uploadCrossfile} data-testid="crossfile-input" />
                        </div>
                        <Button type="button" variant="outline" onClick={downloadMappingExample} data-testid="download-mapping-example">
                          <Download className="w-4 h-4 mr-2" />
                          CSV Example
                        </Button>
                      </div>
                      {formData.crossfile_id && (
                        <div className="rounded-lg border bg-white p-4 space-y-4" data-testid="mapping-editor">
                          <div className="flex flex-wrap items-start justify-between gap-3">
                            <div>
                              <Label className="text-base">Mapping List</Label>
                              <p className="text-xs text-gray-500">
                                Match contacts to file names. Exact file names are preferred; partial names are accepted.
                              </p>
                            </div>
                            <div className="flex items-center gap-2">
                              <span className="rounded-full bg-green-100 px-3 py-1 text-xs font-medium text-green-700">
                                {matchedMappingCount} matched
                              </span>
                              <span className="rounded-full bg-red-100 px-3 py-1 text-xs font-medium text-red-700">
                                {mappedPreviewRows.length - matchedMappingCount} missing
                              </span>
                              <Button type="button" size="sm" variant="outline" onClick={saveAllMappingRows} disabled={mappingBusy || mappedPreviewRows.length === 0} data-testid="save-all-mapping-rows">
                                <Save className="w-4 h-4 mr-2" />
                                Save All
                              </Button>
                              <Button type="button" size="sm" variant="outline" onClick={confirmDeleteMapping} disabled={mappingBusy}>
                                <Trash2 className="w-4 h-4 mr-2" />
                                Delete Mapping
                              </Button>
                            </div>
                          </div>

                          <div className="grid md:grid-cols-[1fr_1fr_auto] gap-2 items-end rounded-md bg-gray-50 p-3">
                            <div>
                              <Label className="text-xs">Recipient Email</Label>
                              <Input
                                value={newMappingRow.recipient_email}
                                onChange={(e) => setNewMappingRow({...newMappingRow, recipient_email: e.target.value})}
                                placeholder="person@example.com"
                                data-testid="new-mapping-email"
                              />
                            </div>
                            <div>
                              <Label className="text-xs">File Name Match</Label>
                              <Input
                                value={newMappingRow.nameinfile}
                                onChange={(e) => setNewMappingRow({...newMappingRow, nameinfile: e.target.value})}
                                placeholder="invoice_001.pdf"
                                data-testid="new-mapping-file"
                              />
                            </div>
                            <Button type="button" onClick={addMappingRow} disabled={mappingBusy} data-testid="add-mapping-row">
                              <Plus className="w-4 h-4 mr-2" />
                              Add
                            </Button>
                          </div>

                          <div className="max-h-80 overflow-y-auto rounded-md border">
                            {mappedPreviewRows.length === 0 && (
                              <div className="p-4 text-sm text-gray-500">No mapping rows yet.</div>
                            )}
                            {mappedPreviewRows.map((row) => {
                              const draft = rowDrafts[row.id] || { recipient_email: row.recipient_email, nameinfile: row.nameinfile };
                              return (
                                <div key={row.id} className="grid gap-2 border-b p-3 last:border-b-0 md:grid-cols-[1fr_1fr_auto] md:items-end">
                                  <div>
                                    <Label className="text-xs">Recipient</Label>
                                    <Input
                                      value={draft.recipient_email || ''}
                                      onChange={(e) => updateRowDraft(row.id, 'recipient_email', e.target.value)}
                                      data-testid={`mapping-email-${row.id}`}
                                    />
                                  </div>
                                  <div>
                                    <Label className="text-xs">File Name Match</Label>
                                    <Input
                                      value={draft.nameinfile || ''}
                                      onChange={(e) => updateRowDraft(row.id, 'nameinfile', e.target.value)}
                                      data-testid={`mapping-file-${row.id}`}
                                    />
                                    <div className={`mt-1 flex items-center gap-2 text-xs ${row.match_status === 'matched' ? 'text-gray-600' : 'text-red-600'}`}>
                                      <FileText className="h-3.5 w-3.5" />
                                      <span className="break-all">{row.file_name}</span>
                                      <span className={`rounded-full px-2 py-0.5 ${row.match_status === 'matched' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                                        {row.match_status}
                                      </span>
                                    </div>
                                  </div>
                                  <div className="flex gap-2">
                                    <Button type="button" size="icon" variant="outline" onClick={() => saveMappingRow(row)} disabled={mappingBusy} title="Save mapping row">
                                      <Save className="h-4 w-4" />
                                    </Button>
                                    <Button type="button" size="icon" variant="outline" onClick={() => confirmDeleteMappingRow(row)} disabled={mappingBusy} title="Remove mapping row">
                                      <X className="h-4 w-4" />
                                    </Button>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}

              {sourceNeedsRecipients && (
                <div>
                  <Label>Recipients</Label>
                  <Textarea
                    value={formData.recipients}
                    onChange={(e) => setFormData({...formData, recipients: e.target.value})}
                    placeholder="person@example.com, other@example.com"
                    rows={4}
                    required
                    data-testid="recipients-input"
                  />
                </div>
              )}

              {formData.source_type !== 'manual' && (
                <div>
                  <Label>Delivery Mode</Label>
                  <Select value={formData.mode} onValueChange={(v) => setFormData({...formData, mode: v})}>
                    <SelectTrigger data-testid="mode-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="attachment">Attachment</SelectItem>
                      <SelectItem value="link">Download Link</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}

              {shouldShowPreview && (
                <div className="rounded-lg border bg-white p-4 space-y-3" data-testid="send-file-tree-preview">
                  <div className="flex items-center justify-between gap-3">
                    <div>
                      <Label className="text-base">Send Preview</Label>
                      <p className="text-xs text-gray-500">Recipients and the file that will be sent to each contact.</p>
                    </div>
                    <div className="rounded-full bg-blue-50 px-3 py-1 text-xs font-medium text-blue-700">
                      {formData.source_type === 'single_file' ? recipientPreview.length : mappedPreviewRows.length} contacts
                    </div>
                  </div>
                  <div className="max-h-72 overflow-y-auto rounded-md border">
                    {formData.source_type === 'single_file' && recipientPreview.map((recipient) => (
                      <div key={recipient} className="border-b last:border-b-0 p-3">
                        <div className="flex items-start gap-3">
                          <Mail className="mt-0.5 h-4 w-4 text-blue-600" />
                          <div className="min-w-0 flex-1">
                            <div className="font-medium text-sm break-all">{recipient}</div>
                            <div className="mt-1 ml-4 flex items-center gap-2 text-xs text-gray-600">
                              <FileText className="h-3.5 w-3.5" />
                              <span className="break-all">{selectedSingleFile.original_filename}</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                    {formData.source_type === 'mapped_files' && mappedPreviewRows.map((row) => (
                      <div key={row.id} className="border-b last:border-b-0 p-3">
                        <div className="flex items-start gap-3">
                          <Mail className={`mt-0.5 h-4 w-4 ${row.match_status === 'matched' ? 'text-blue-600' : 'text-red-600'}`} />
                          <div className="min-w-0 flex-1">
                            <div className="font-medium text-sm break-all">{row.recipient_email}</div>
                            <div className={`mt-1 ml-4 flex items-center gap-2 text-xs ${row.match_status === 'matched' ? 'text-gray-600' : 'text-red-600'}`}>
                              <FileText className="h-3.5 w-3.5" />
                              <span className="break-all">{row.file_name}</span>
                              <span className={`rounded-full px-2 py-0.5 ${row.match_status === 'matched' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                                {row.match_status}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label>Subject</Label>
                  <Input value={formData.subject} onChange={(e) => setFormData({...formData, subject: e.target.value})} required data-testid="subject-input" />
                </div>
                <div className="flex items-center justify-between rounded-lg border p-3">
                  <div>
                    <Label>Keep Azure sent copy</Label>
                    <p className="text-xs text-gray-500">Applies to Azure Graph sends.</p>
                  </div>
                  <Switch checked={formData.save_copy_on_sent} onCheckedChange={(v) => setFormData({...formData, save_copy_on_sent: v})} data-testid="save-copy-send-switch" />
                </div>
              </div>
              <div>
                <Label>Body HTML</Label>
                <Textarea value={formData.body_html} onChange={(e) => setFormData({...formData, body_html: e.target.value})} rows={7} required data-testid="body-input" />
              </div>
              <Button type="submit" className="w-full" disabled={!formData.mail_config_id} data-testid="save-draft-button">
                {editingBatch ? 'Update Draft' : 'Create Draft'}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <Card className="glass-card" data-testid="mappings-group">
        <CardHeader>
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <CardTitle className="flex items-center gap-2">
                <FileText className="w-5 h-5" />
                Mappings
              </CardTitle>
              <p className="mt-1 text-sm text-gray-600">See existing mappings and maintain contact-to-file matches without creating a send batch.</p>
            </div>
            <div className="flex flex-wrap gap-2">
              <Button type="button" variant="outline" onClick={downloadMappingExample} data-testid="standalone-download-mapping-example">
                <Download className="w-4 h-4 mr-2" />
                CSV Example
              </Button>
              <Button type="button" onClick={() => setShowAddMappingForm(value => !value)} data-testid="toggle-add-mapping-form">
                {showAddMappingForm ? <X className="w-4 h-4 mr-2" /> : <Plus className="w-4 h-4 mr-2" />}
                {showAddMappingForm ? 'Close' : 'Add Mapping'}
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="rounded-lg border bg-white p-4 space-y-3" data-testid="existing-mappings-list">
            <div className="flex items-center justify-between gap-3">
              <div>
                <Label className="text-base">Existing Mappings</Label>
                <p className="text-xs text-gray-500">Open any map here to edit its contacts and file matches.</p>
              </div>
              <span className="rounded-full bg-blue-50 px-3 py-1 text-xs font-medium text-blue-700">
                {allCrossfiles.length} maps
              </span>
            </div>
            {allCrossfiles.length === 0 ? (
              <div className="rounded-md border border-dashed bg-gray-50 p-4 text-sm text-gray-600">
                No mappings uploaded yet.
              </div>
            ) : (
              <div className="max-h-72 overflow-y-auto rounded-md border">
                {allCrossfiles.map(cf => {
                  const batch = batchById[cf.batch_id];
                  return (
                    <div key={cf.id} className="grid gap-3 border-b p-3 last:border-b-0 md:grid-cols-[1fr_auto] md:items-center" data-testid={`existing-mapping-${cf.id}`}>
                      <div className="min-w-0">
                        <div className="flex flex-wrap items-center gap-2">
                          <div className="font-medium break-all">{cf.original_filename}</div>
                          <span className="rounded-full bg-green-100 px-2 py-0.5 text-xs text-green-700">{cf.matched_count} matched</span>
                          <span className="rounded-full bg-red-100 px-2 py-0.5 text-xs text-red-700">{cf.missing_count} missing</span>
                        </div>
                        <div className="mt-1 text-xs text-gray-500">
                          Batch: {batch?.original_zip_name || 'Single File'} · Rows: {cf.total_rows} · Uploaded: {new Date(cf.created_at).toLocaleDateString()}
                        </div>
                      </div>
                      <Button type="button" size="sm" variant={mapCrossfileId === cf.id ? 'default' : 'outline'} onClick={() => editExistingMapping(cf)} data-testid={`edit-existing-mapping-${cf.id}`}>
                        <Pencil className="w-4 h-4 mr-2" />
                        Edit Map
                      </Button>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {showAddMappingForm && (
            <div className="rounded-lg border bg-white p-4 space-y-3">
              <div className="flex items-center justify-between gap-3">
                <div>
                  <Label className="text-base">Add Mapping</Label>
                  <p className="text-xs text-gray-500">Choose a file batch and upload a CSV map.</p>
                </div>
                <Button type="button" size="sm" variant="outline" onClick={() => setShowAddMappingForm(false)} data-testid="close-add-mapping-form">
                  <X className="w-4 h-4 mr-2" />
                  Close
                </Button>
              </div>
              <div className="grid gap-3 md:grid-cols-[1fr_1fr]">
              <div>
                <Label>File Batch</Label>
                <Select value={mapBatchId} onValueChange={handleStandaloneBatchSelect}>
                  <SelectTrigger data-testid="standalone-map-batch-select"><SelectValue placeholder="Choose file batch..." /></SelectTrigger>
                  <SelectContent>
                    {batches.map(b => <SelectItem key={b.id} value={b.id}>{b.original_zip_name || 'Single File'} ({b.file_count} files)</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Upload New Map</Label>
                <Input type="file" accept=".csv" onChange={uploadStandaloneCrossfile} disabled={!mapBatchId} data-testid="standalone-crossfile-input" />
              </div>
              </div>
            </div>
          )}

          {mapBatchId && (
            <div className="grid gap-3 md:grid-cols-[1fr_auto] md:items-end">
              <div>
                <Label>Uploaded Maps</Label>
                <Select value={mapCrossfileId} onValueChange={handleStandaloneCrossfileSelect}>
                  <SelectTrigger data-testid="standalone-crossfile-select"><SelectValue placeholder="Choose a map to edit..." /></SelectTrigger>
                  <SelectContent>
                    {mapCrossfiles.map(cf => (
                      <SelectItem key={cf.id} value={cf.id}>
                        {cf.original_filename} ({cf.matched_count} matched, {cf.missing_count} missing)
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <Button type="button" variant="outline" onClick={confirmDeleteStandaloneMapping} disabled={!mapCrossfileId || mappingBusy} data-testid="standalone-delete-mapping">
                <Trash2 className="w-4 h-4 mr-2" />
                Delete Map
              </Button>
            </div>
          )}

          {mapBatchId && mapCrossfiles.length === 0 && (
            <div className="rounded-md border border-dashed bg-white/70 p-4 text-sm text-gray-600">
              No maps uploaded for this file batch yet.
            </div>
          )}

          {mapCrossfileId && (
            <div className="rounded-lg border bg-white p-4 space-y-4" data-testid="standalone-mapping-editor">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <Label className="text-base">Contacts and File Matches</Label>
                  <p className="text-xs text-gray-500">Edit the recipient email or the file name match, then save the row to rematch it.</p>
                </div>
                <div className="flex flex-wrap gap-2">
                  <span className="rounded-full bg-green-100 px-3 py-1 text-xs font-medium text-green-700">
                    {standaloneMatchedCount} matched
                  </span>
                  <span className="rounded-full bg-red-100 px-3 py-1 text-xs font-medium text-red-700">
                    {standalonePreviewRows.length - standaloneMatchedCount} missing
                  </span>
                  <Button type="button" size="sm" variant="outline" onClick={saveAllStandaloneRows} disabled={mappingBusy || standalonePreviewRows.length === 0} data-testid="standalone-save-all-mapping-rows">
                    <Save className="w-4 h-4 mr-2" />
                    Save All
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={() => {
                      setMapCrossfileId('');
                      setStandaloneRows([]);
                      setStandaloneDrafts({});
                    }}
                    data-testid="close-mapping-editor"
                  >
                    <X className="w-4 h-4 mr-2" />
                    Close
                  </Button>
                </div>
              </div>

              <div className="grid gap-2 rounded-md bg-gray-50 p-3 md:grid-cols-[1fr_1fr_auto] md:items-end">
                <div>
                  <Label className="text-xs">Recipient Email</Label>
                  <Input
                    value={standaloneNewRow.recipient_email}
                    onChange={(e) => setStandaloneNewRow({...standaloneNewRow, recipient_email: e.target.value})}
                    placeholder="person@example.com"
                    data-testid="standalone-new-mapping-email"
                  />
                </div>
                <div>
                  <Label className="text-xs">File Name Match</Label>
                  <Input
                    value={standaloneNewRow.nameinfile}
                    onChange={(e) => setStandaloneNewRow({...standaloneNewRow, nameinfile: e.target.value})}
                    placeholder="invoice_001.pdf"
                    data-testid="standalone-new-mapping-file"
                  />
                </div>
                <Button type="button" onClick={addStandaloneRow} disabled={mappingBusy} data-testid="standalone-add-mapping-row">
                  <Plus className="w-4 h-4 mr-2" />
                  Add
                </Button>
              </div>

              <div className="max-h-96 overflow-y-auto rounded-md border">
                {standalonePreviewRows.length === 0 && (
                  <div className="p-4 text-sm text-gray-500">No contacts in this map yet.</div>
                )}
                {standalonePreviewRows.map((row) => {
                  const draft = standaloneDrafts[row.id] || { recipient_email: row.recipient_email, nameinfile: row.nameinfile };
                  return (
                    <div key={row.id} className="grid gap-2 border-b p-3 last:border-b-0 md:grid-cols-[1fr_1fr_auto] md:items-end">
                      <div>
                        <Label className="text-xs">Recipient</Label>
                        <Input
                          value={draft.recipient_email || ''}
                          onChange={(e) => updateStandaloneDraft(row.id, 'recipient_email', e.target.value)}
                          data-testid={`standalone-mapping-email-${row.id}`}
                        />
                      </div>
                      <div>
                        <Label className="text-xs">File Name Match</Label>
                        <Input
                          value={draft.nameinfile || ''}
                          onChange={(e) => updateStandaloneDraft(row.id, 'nameinfile', e.target.value)}
                          data-testid={`standalone-mapping-file-${row.id}`}
                        />
                        <div className={`mt-1 flex items-center gap-2 text-xs ${row.match_status === 'matched' ? 'text-gray-600' : 'text-red-600'}`}>
                          <FileText className="h-3.5 w-3.5" />
                          <span className="break-all">{row.file_name}</span>
                          <span className={`rounded-full px-2 py-0.5 ${row.match_status === 'matched' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                            {row.match_status}
                          </span>
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <Button type="button" size="icon" variant="outline" onClick={() => saveStandaloneRow(row)} disabled={mappingBusy} title="Save mapping row">
                          <Save className="h-4 w-4" />
                        </Button>
                        <Button type="button" size="icon" variant="outline" onClick={() => confirmDeleteStandaloneRow(row)} disabled={mappingBusy} title="Remove mapping row">
                          <X className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      <Card className="glass-card" data-testid="file-batches-group">
        <CardHeader>
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Package className="w-5 h-5" />
                File Batches
              </CardTitle>
              <p className="mt-1 text-sm text-gray-600">Files available for one-file or mapped sends.</p>
            </div>
            <Button type="button" variant="outline" onClick={() => navigate('/batches')}>
              <Plus className="w-4 h-4 mr-2" />
              Manage Files
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {batches.length === 0 ? (
            <div className="rounded-md border border-dashed bg-white/70 p-4 text-sm text-gray-600">
              No file batches uploaded yet.
            </div>
          ) : (
            <div className="grid gap-3 md:grid-cols-3">
              {batches.map(batch => (
                <div key={batch.id} className="rounded-lg border bg-white p-4" data-testid={`combined-file-batch-${batch.id}`}>
                  <div className="flex items-start gap-3">
                    {batch.original_zip_name ? <FileArchive className="mt-0.5 h-5 w-5 text-blue-600" /> : <Package className="mt-0.5 h-5 w-5 text-blue-600" />}
                    <div className="min-w-0 flex-1">
                      <div className="font-medium break-all">{batch.original_zip_name || 'Single File'}</div>
                      <div className="mt-2 grid gap-1 text-sm text-gray-600">
                        <div className="flex justify-between gap-3"><span>Files</span><span className="font-medium text-gray-900">{batch.file_count}</span></div>
                        <div className="flex justify-between gap-3"><span>Size</span><span className="font-medium text-gray-900">{formatBytes(batch.total_size_bytes)}</span></div>
                        <div className="flex justify-between gap-3"><span>Uploaded</span><span className="font-medium text-gray-900">{new Date(batch.created_at).toLocaleDateString()}</span></div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Send Batch</AlertDialogTitle>
            <AlertDialogDescription>
              Delete "{deletingBatch?.subject || 'No subject'}" and its recipient tracking history? Sending batches must be paused before they can be deleted.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={deleteSendBatch} className="bg-red-600 hover:bg-red-700">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={mappingDeleteDialogOpen} onOpenChange={setMappingDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {mappingDeleteTarget?.type === 'row' ? 'Remove Mapping Row' : 'Delete Mapping'}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {mappingDeleteTarget?.type === 'row'
                ? `Remove the mapping for ${mappingDeleteTarget?.row?.recipient_email || 'this contact'}?`
                : 'Delete this mapping and all its rows? Mappings already used by send batches cannot be deleted.'}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={mappingBusy}>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={deleteMappingTarget} disabled={mappingBusy} className="bg-red-600 hover:bg-red-700">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <section className="space-y-4" data-testid="send-batches-group">
        <div>
          <h2 className="flex items-center gap-2 text-2xl font-bold text-white" style={{fontFamily: 'Space Grotesk'}}>
            <Send className="w-5 h-5" />
            Send Batches
          </h2>
          <p className="mt-1 text-sm text-white/80">Drafts, queued sends, and background sending progress.</p>
        </div>
        <div className="grid gap-6">
        {sendBatches.length === 0 && (
          <div className="rounded-md border border-dashed bg-white/70 p-4 text-sm text-gray-600">
            No send batches created yet.
          </div>
        )}
        {sendBatches.map(batch => {
          const processedCount = batch.processed_count ?? ((batch.sent_count || 0) + (batch.failed_count || 0) + (batch.skipped_count || 0));
          const progress = batch.progress_percent ?? (batch.total_recipients > 0 ? (processedCount / batch.total_recipients) * 100 : 0);
          const editable = ['draft', 'queued', 'paused'].includes(batch.status);
          return (
            <Card key={batch.id} className="glass-card cursor-pointer hover:shadow-xl transition-shadow" onClick={() => navigate(`/send-batches/${batch.id}`)} data-testid={`send-batch-card-${batch.id}`}>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="flex items-center gap-2">
                    <Send className="w-5 h-5" />
                    {batch.subject || 'No subject'}
                  </CardTitle>
                  <div className={`status-badge status-${batch.status}`}>{batch.status}</div>
                </div>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex flex-wrap gap-2 text-xs">
                  <span className="rounded-full bg-gray-100 px-3 py-1 text-gray-700">{batch.source_type || 'mapped_files'}</span>
                  <span className="rounded-full bg-gray-100 px-3 py-1 text-gray-700">{batch.mode}</span>
                  {batch.save_copy_on_sent && <span className="rounded-full bg-blue-100 px-3 py-1 text-blue-700">Azure copy</span>}
                </div>
                <div className="grid grid-cols-5 gap-4 text-sm">
                  <div><div className="text-gray-600">Total</div><div className="font-medium text-lg">{batch.total_recipients}</div></div>
                  <div><div className="text-gray-600">Processed</div><div className="font-medium text-lg text-purple-600">{processedCount}</div></div>
                  <div><div className="text-gray-600">Sent</div><div className="font-medium text-lg text-green-600">{batch.sent_count}</div></div>
                  <div><div className="text-gray-600">Failed</div><div className="font-medium text-lg text-red-600">{batch.failed_count}</div></div>
                  <div><div className="text-gray-600">Pending</div><div className="font-medium text-lg text-blue-600">{batch.pending_count}</div></div>
                </div>
                <div>
                  <div className="flex justify-between text-sm mb-1">
                    <span>{batch.status === 'sending' ? 'Sending in background' : 'Progress'}</span>
                    <span>{processedCount} / {batch.total_recipients} ({Number(progress).toFixed(0)}%)</span>
                  </div>
                  <Progress value={progress} className="h-2" />
                </div>
                <div className="flex flex-wrap gap-2">
                  {editable && (
                    <Button size="sm" variant="outline" onClick={(e) => { e.stopPropagation(); editBatch(batch); }} data-testid={`edit-batch-${batch.id}`}>
                      <Pencil className="w-4 h-4 mr-2" />
                      Edit
                    </Button>
                  )}
                  {batch.status !== 'sending' && (
                    <Button size="sm" variant="outline" onClick={(e) => { e.stopPropagation(); confirmDeleteBatch(batch); }} data-testid={`delete-send-batch-${batch.id}`}>
                      <Trash2 className="w-4 h-4 mr-2" />
                      Delete
                    </Button>
                  )}
                  {batch.status === 'draft' && (
                    <Button size="sm" onClick={(e) => { e.stopPropagation(); queueBatch(batch.id); }} data-testid={`queue-batch-${batch.id}`}>
                      <ListChecks className="w-4 h-4 mr-2" />
                      Queue
                    </Button>
                  )}
                  {batch.status === 'queued' && (
                    <Button size="sm" onClick={(e) => { e.stopPropagation(); sendBatch(batch.id); }} data-testid={`send-batch-${batch.id}`}>
                      <Play className="w-4 h-4 mr-2" />
                      Send
                    </Button>
                  )}
                  {(batch.status === 'sending') && (
                    <Button size="sm" variant="outline" disabled>
                      <Pause className="w-4 h-4 mr-2" />
                      Sending
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          );
        })}
        </div>
      </section>
    </div>
  );
}
